import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { getACMGVerdictClassifications } from "../../../../state/selectors/CardsUI.selectors";
import CardWrapper from "../../CardWrapper";
import Card from "../../Card";
import { HorizontalStackedBar } from "../../../atoms";
import { getPredictionFromScore } from "../../../../pages/variant/components/InSilicoPredictions/InSilicoPredictionsHelpers";
import {
  getEveScorePathogenicityType,
  getInSilicoThresholds,
  getNewInSilicoThresholds,
} from "../../../../state/selectors/general.selectors";
import * as constants from "../../../../pages/variant/components/InSilicoPredictions/InSilicoPredictionsConstants";
import {
  getDbnsfp,
  getDann,
  getDbnsfpDbscsnv,
  getDbnsfpPremium,
  getCadd,
} from "../../../../state/selectors/InSilicoPredictions.selector";
import { inSilicoDbsList } from "../../../../pages/variant/components/InSilicoPredictions/inSilicoDbsList";
import {
  pathogenicStrongColor,
  uncertainSignificanceColor,
  benignStrongColor,
  uncertainColor,
} from "../../../../common/helpers/colors";
import {
  isThaliaVersionSmallerThanElevenPointFour,
  getAcmgAnnotation,
} from "../../../../state/selectors/ACMGComponent.selectors";
import { getAllInsilcoData } from "../../../../pages/variant/components/InSilicoPredictions/scoresBasedInsilicoPredictions/selectors/inSilicoPredictionScoresBased.selectors";
import IndividualPredictionsBarWrapper from "../../../../pages/variant/components/InSilicoPredictions/scoresBasedInsilicoPredictions/components/IndividualPredictionsBarWrapper";
import { getInsilicoDbList } from "../../../../pages/variant/components/InSilicoPredictions/scoresBasedInsilicoPredictions/helpers/getInSilicoDbList";
import MetaScoresBarchart from "../../../../pages/variant/components/InSilicoPredictions/scoresBasedInsilicoPredictions/components/MetaScoresBarchart";
import { colorInSilicoCardRuleStrength } from "./helpers";
import {
  getMetaScoresDbs,
  countPathogenicDbsSum,
  countBenignDbsSum,
  countUncertainDbsSum,
  getIndividualDbs,
} from "../../../../pages/variant/components/InSilicoPredictions/scoresBasedInsilicoPredictions/helpers/genericHelpers";

const PathogenicityScoresCard = ({
  card,
  dbnsfp,
  thresholds,
  newThresholds,
  dann,
  cadd,
  dbnsfpDbscsnv,
  evePathogenicityType,
  shouldShowNewComponent,
  insilicoData,
  acmgAnnotation,
}) => {
  const [pathogenicityCounts, setPathogenicityCounts] = useState(null);
  const [insilicoDbs, setInsilicoDbs] = useState([]);

  useEffect(() => {
    if (insilicoData && thresholds) {
      setInsilicoDbs(getInsilicoDbList(insilicoData, thresholds));
    }
  }, [insilicoData, thresholds]);

  const getOrderDependingOfCategory = (type) => {
    if (constants.PREDICTION_PATHOGENIC_GROUP.includes(type)) {
      return 0;
    }

    if (constants.PREDICTION_UNCERTAIN_GROUP.includes(type)) {
      return 1;
    }

    if (constants.PREDICTION_BENIGH_GROUP.includes(type)) {
      return 2;
    }
    return 3;
    // Debug to avoid returning undefined to pathoCounts
  };

  const pathogenicityScoresContent = () => {
    return pathogenicityCounts ? (
      <div className="tw-text-white">
        <HorizontalStackedBar
          className="tw-h-3 tw-text-xs tw-min-w-stackedBar "
          data={pathogenicityCounts}
        />
      </div>
    ) : null;
  };

  useEffect(() => {
    if (!thresholds || shouldShowNewComponent) {
      return () => {};
    }
    const finalResults = [
      {
        name: "Pathogenic",
        totalCount: 0,
        identifier: 0,
      },
      {
        name: "Uncertain Significance",
        totalCount: 0,
        identifier: 1,
      },
      {
        name: "Benign",
        totalCount: 0,
        identifier: 2,
      },
      {
        name: "Not found pathogenicities",
        totalCount: 0,
        identifier: 3,
      },
    ];
    if (evePathogenicityType !== 3) {
      finalResults[evePathogenicityType].totalCount += 1;
    }
    const dbList = inSilicoDbsList(thresholds);
    dbList.forEach((item) => {
      if (item.type === "dbItems" && Array.isArray(dbnsfp)) {
        const pred = `${item.prefix}_pred`;
        const score = `${item.prefix}_score`;
        const predLetterObj = dbnsfp[0][pred];
        const predFromScore = getPredictionFromScore(
          item.prefix,
          dbnsfp[0][score],
          item.thresholds
        );
        const hasPrediction = predLetterObj || predFromScore;
        const predictionOrder = predLetterObj
          ? getOrderDependingOfCategory(
              item.prediction[predLetterObj?.filter((val) => val !== null)?.[0]]
            )
          : getOrderDependingOfCategory(predFromScore);

        if (hasPrediction && !item.isMeta) {
          finalResults[predictionOrder].totalCount += 1;
        }
      }
      if (item.type === "cadd" && Array.isArray(cadd)) {
        const predFromScore = getPredictionFromScore(
          item.prefix,
          cadd[0].cadd_score,
          item.thresholds
        );
        const order = getOrderDependingOfCategory(predFromScore);
        if (order !== undefined) {
          finalResults[order].totalCount += 1;
        }
      }
      if (item.type === "dann" && Array.isArray(dann)) {
        const predFromScore = getPredictionFromScore(
          item.prefix,
          dann[0].dann_score,
          item.thresholds
        );
        const order = getOrderDependingOfCategory(predFromScore);
        if (order !== undefined) {
          finalResults[order].totalCount += 1;
        }
      }
      if (item.type === "aloft" && Array.isArray(dbnsfp)) {
        if (dbnsfp[0].aloft_pred) {
          const order = getOrderDependingOfCategory(dbnsfp[0].aloft_pred[0]);
          finalResults[order].totalCount += 1;
        }
      }
      if (
        (item.type === "eigen" || item.type === "eigenpc") &&
        Array.isArray(dbnsfp)
      ) {
        let eigenCoding;
        let eigenCodename;

        if (item.type === "eigen") {
          eigenCoding = "eigen_raw_coding";
          eigenCodename = "eigen";
        }

        if (item.type === "eigenpc") {
          eigenCoding = "eigen_pc_raw_coding";
          eigenCodename = "eigen_pc";
        }

        const predFromScore = getPredictionFromScore(
          eigenCodename,
          dbnsfp[0][eigenCoding],
          thresholds
        );

        if (predFromScore) {
          const order = getOrderDependingOfCategory(predFromScore);
          finalResults[order].totalCount += 1;
        }
      }
      if (item.type === "dbscSNV") {
        if (
          !Array.isArray(dbnsfpDbscsnv) ||
          (Array.isArray(dbnsfpDbscsnv) && dbnsfpDbscsnv.length === 0)
        )
          return;
        const predFromScore = getPredictionFromScore(
          item.prefix,
          dbnsfpDbscsnv[0].ada_score,
          item.thresholds
        );
        const order = getOrderDependingOfCategory(predFromScore);
        if (order !== undefined) {
          finalResults[order].totalCount += 1;
        }
      }
    });

    if (
      finalResults[0].totalCount === 0 &&
      finalResults[1].totalCount === 0 &&
      finalResults[2].totalCount === 0
    ) {
      setPathogenicityCounts(null);
      return () => {};
    }

    setPathogenicityCounts([
      {
        value: finalResults[0].totalCount,
        color: pathogenicStrongColor,
        text: `${finalResults[0].totalCount}`,
      },
      {
        value: finalResults[1].totalCount,
        color: uncertainSignificanceColor,
        text: `${finalResults[1].totalCount}`,
      },
      {
        value: finalResults[2].totalCount,
        color: benignStrongColor,
        text: `${finalResults[2].totalCount}`,
      },
    ]);
    return () => setPathogenicityCounts(null);
  }, [thresholds, dbnsfp, dann, cadd, shouldShowNewComponent]);

  useEffect(() => {
    if (!shouldShowNewComponent) return;
    if (insilicoData && newThresholds) {
      setInsilicoDbs(getInsilicoDbList(insilicoData, newThresholds));
    }
  }, [shouldShowNewComponent, insilicoData, newThresholds]);

  const allRules = acmgAnnotation?.verdict?.classifications;
  const displayedRules = allRules?.map((i) => {
    if (i.includes("PP3")) {
      return (
        <span
          key={`${new Date().getTime()}-${i}`}
          className="tw-font-bold tw-text-blueGray-500"
          data-testid="pp3-rule-found"
        >
          PP3:&nbsp;
          {i.substring(4) ? (
            <span
              className="tw-font-bold"
              style={{ color: colorInSilicoCardRuleStrength(i) }}
            >
              Pathogenic&nbsp;{i.substring(4)}
            </span>
          ) : (
            <span
              className="tw-font-bold"
              style={{ color: colorInSilicoCardRuleStrength("PP3_Supporting") }}
            >
              Supporting
            </span>
          )}
        </span>
      );
    }
    if (i.includes("BP4")) {
      return (
        <span
          key={`${new Date().getTime()}-${i}`}
          className="tw-font-bold tw-text-blueGray-500"
          data-testid="bp4-rule-found"
        >
          BP4:&nbsp;
          {i.substring(4) ? (
            <span
              className="tw-font-bold"
              style={{ color: colorInSilicoCardRuleStrength(i) }}
            >
              Benign&nbsp;{i.substring(4)}
            </span>
          ) : (
            <span
              key={`${new Date().getTime()}-${i}`}
              className="tw-font-bold"
              style={{ color: colorInSilicoCardRuleStrength("BP4_Supporting") }}
            >
              Supporting
            </span>
          )}
        </span>
      );
    }
    return null;
  });

  const metaScoresDbs = getMetaScoresDbs(insilicoDbs);
  const metaPathogenicSum = countPathogenicDbsSum(metaScoresDbs);
  const metaBenignSum = countBenignDbsSum(metaScoresDbs);
  const metaUncertainSum = countUncertainDbsSum(metaScoresDbs);

  const individualDbs = getIndividualDbs(insilicoDbs);
  const individualPathogenicSum = countPathogenicDbsSum(individualDbs);
  const individualBenignSum = countBenignDbsSum(individualDbs);
  const individualUncertainSum = countUncertainDbsSum(individualDbs);

  const globalPathogenicSum = metaPathogenicSum + individualPathogenicSum;
  const globalUncertainSum = metaUncertainSum + individualUncertainSum;
  const globalBenignSum = metaBenignSum + individualBenignSum;

  const getBackgroundColor = () => {
    if (
      globalPathogenicSum > globalBenignSum &&
      globalPathogenicSum > globalUncertainSum
    ) {
      return pathogenicStrongColor;
    }
    if (
      globalBenignSum > globalPathogenicSum &&
      globalBenignSum > globalUncertainSum
    ) {
      return benignStrongColor;
    }

    return uncertainColor;
  };

  return (
    <span data-testid="test-side-color">
      <CardWrapper
        {...card}
        card={card}
        isDisabled={
          (!shouldShowNewComponent && !Array.isArray(pathogenicityCounts)) ||
          (shouldShowNewComponent &&
            Object.values(insilicoData).every((elem) => elem === undefined))
        }
        secondaryTitle={
          !Object.values(insilicoData).every((elem) => elem === undefined)
            ? displayedRules
            : null
        }
        color={getBackgroundColor()}
      >
        {shouldShowNewComponent ? (
          <div
            className="tw-text-white"
            data-tooltip="Individual Predictions"
            data-testid="insilico-data-content"
          >
            <IndividualPredictionsBarWrapper
              insilicoDbs={insilicoDbs}
              forceDisabledToolTip={true}
              customClass="tw-h-3 tw-text-xs"
            />
            <div className="tw-mt-3 tw-text-xs" data-tooltip="Meta Scores">
              <MetaScoresBarchart
                insilicoDbs={insilicoDbs}
                forceDisabledToolTip={true}
              />{" "}
            </div>
          </div>
        ) : (
          <div>{pathogenicityScoresContent()}</div>
        )}
      </CardWrapper>
    </span>
  );
};

const { arrayOf, string, number, instanceOf } = PropTypes;

PathogenicityScoresCard.propTypes = {
  card: instanceOf(Card).isRequired,
  acmgRules: arrayOf(string),
  acmgAnnotation: PropTypes.oneOfType([
    PropTypes.shape(),
    PropTypes.arrayOf(PropTypes.shape()),
  ]),
  shouldShowNewComponent: PropTypes.bool,
  insilicoData: PropTypes.shape({}),
  thresholds: PropTypes.shape({}),
  newThresholds: PropTypes.arrayOf(PropTypes.shape({})),
  dbnsfp: PropTypes.arrayOf(
    PropTypes.shape({
      aloft_pred: PropTypes.arrayOf(PropTypes.string),
      version: PropTypes.string,
      ensembl_proteinid: PropTypes.arrayOf(PropTypes.string),
      ensembl_transcriptid: PropTypes.arrayOf(PropTypes.string),
      mutationtaster_pred: PropTypes.arrayOf(PropTypes.string),
      mutationtaster_score: PropTypes.arrayOf(PropTypes.number),
      phylop100way_vertebrate: PropTypes.arrayOf(PropTypes.number),
      mutationtaster_converted_rankscore: PropTypes.arrayOf(PropTypes.number),
      mutationassessor_pred: PropTypes.arrayOf(PropTypes.string),
      mutationassessor_score: PropTypes.arrayOf(PropTypes.number),
      mutationassessor_rankscore: PropTypes.arrayOf(PropTypes.number),
      fathmm_mkl_coding_pred: PropTypes.arrayOf(PropTypes.string),
      fathmm_mkl_coding_score: PropTypes.arrayOf(PropTypes.number),
      fathmm_mkl_coding_rankscore: PropTypes.arrayOf(PropTypes.number),
      metasvm_pred: PropTypes.arrayOf(PropTypes.string),
      metasvm_score: PropTypes.arrayOf(PropTypes.number),
      metasvm_rankscore: PropTypes.arrayOf(PropTypes.number),
      metalr_pred: PropTypes.arrayOf(PropTypes.string),
      metalr_score: PropTypes.arrayOf(PropTypes.number),
      metalr_rankscore: PropTypes.arrayOf(PropTypes.number),
      lrt_pred: PropTypes.arrayOf(PropTypes.string),
      lrt_score: PropTypes.arrayOf(PropTypes.number),
      lrt_converted_rankscore: PropTypes.arrayOf(PropTypes.number),
      lrt_omega: PropTypes.arrayOf(PropTypes.number),
      gm12878_confidence_value: PropTypes.arrayOf(PropTypes.number),
      gm12878_fitcons_score: PropTypes.arrayOf(PropTypes.number),
      gm12878_fitcons_rankscore: PropTypes.arrayOf(PropTypes.number),
      siphy_29way_logodds_rankscore: PropTypes.arrayOf(PropTypes.number),
      siphy_29way_pi: PropTypes.arrayOf(PropTypes.number),
      siphy_29way_logodds: PropTypes.number,
      phastcons100way_vertebrate: PropTypes.arrayOf(PropTypes.number),
      phastcons100way_vertebrate_rankscore: PropTypes.arrayOf(PropTypes.number),
      bstatistic: PropTypes.number,
      deogen2_pred: PropTypes.arrayOf(PropTypes.string),
      deogen2_rankscore: PropTypes.number,
      deogen2_score: PropTypes.arrayOf(PropTypes.number),
      eigen_pc_phred_coding: PropTypes.number,
      eigen_pc_raw_coding: PropTypes.number,
      eigen_pc_raw_coding_rankscore: PropTypes.number,
      eigen_raw_coding: PropTypes.number,
      eigen_raw_coding_rankscore: PropTypes.number,
      fathmm_xf_coding_score: PropTypes.number,
      fathmm_xf_coding_rankscore: PropTypes.number,
      fathmm_xf_coding_pred: PropTypes.arrayOf(PropTypes.string),
      integrated_confidence_value: PropTypes.number,
      integrated_fitcons_score: PropTypes.number,
      integrated_fitcons_rankscore: PropTypes.number,
      h1_hesc_confidence_value: PropTypes.number,
      h1_hesc_fitcons_score: PropTypes.number,
      h1_hesc_fitcons_rankscore: PropTypes.number,
      huvec_confidence_value: PropTypes.number,
      huvec_fitcons_score: PropTypes.number,
      huvec_fitcons_rankscore: PropTypes.number,
      phylop17way_primate: PropTypes.number,
      phylop17way_primate_rankscore: PropTypes.number,
      phylop30way_mammalian: PropTypes.number,
      phylop30way_mammalian_rankscore: PropTypes.number,
      phastcons17way_primate: PropTypes.number,
      phastcons17way_primate_rankscore: PropTypes.number,
      phastcons30way_mammalian: PropTypes.number,
      phastcons30way_mammalian_rankscore: PropTypes.number,
      primateai_pred: PropTypes.arrayOf(PropTypes.string),
      primateai_score: PropTypes.arrayOf(PropTypes.number),
      primateai_rankscore: PropTypes.arrayOf(PropTypes.number),
      mpc_score: PropTypes.arrayOf(PropTypes.number),
      mpc_rankscore: PropTypes.number,
      mutpred_score: PropTypes.arrayOf(PropTypes.number),
      mutpred_rankscore: PropTypes.number,
      mvp_score: PropTypes.arrayOf(PropTypes.number),
      mvp_rankscore: PropTypes.number,
      revel_score: PropTypes.arrayOf(PropTypes.number),
      revel_rankscore: PropTypes.number,
      list_s2_pred: PropTypes.arrayOf(PropTypes.string),
      list_s2_score: PropTypes.arrayOf(PropTypes.number),
      list_s2_rankscore: PropTypes.number,
      bayesdel_addaf_pred: PropTypes.arrayOf(PropTypes.string),
      bayesdel_addaf_score: PropTypes.number,
      bayesdel_addaf_rankscore: PropTypes.number,
      bayesdel_noaf_pred: PropTypes.arrayOf(PropTypes.string),
      bayesdel_noaf_score: PropTypes.number,
      bayesdel_noaf_rankscore: PropTypes.number,
      metarnn_pred: PropTypes.arrayOf(PropTypes.string),
      metarnn_score: PropTypes.arrayOf(PropTypes.number),
      metarnn_rankscore: PropTypes.number,
      m_cap_pred: PropTypes.arrayOf(PropTypes.string),
      m_cap_score: PropTypes.number,
      m_cap_rankscore: PropTypes.number,
      dann_score: PropTypes.number,
      dann_rankscore: PropTypes.number,
    })
  ),
  dann: PropTypes.arrayOf(
    PropTypes.shape({
      dann_score: number,
      version: string,
    })
  ),
  cadd: PropTypes.arrayOf(
    PropTypes.shape({
      cadd_score: PropTypes.number,
    })
  ),
  dbnsfpDbscsnv: PropTypes.arrayOf(
    PropTypes.shape({
      ada_score: arrayOf(number),
      rf_score: arrayOf(number),
      version: string,
    })
  ),
  dbnsfpPremium: arrayOf(
    PropTypes.shape({
      version: PropTypes.string,
      ensembl_proteinid: PropTypes.arrayOf(PropTypes.string),
      ensembl_transcriptid: PropTypes.arrayOf(PropTypes.string),
      polyphen2_hdiv_pred: PropTypes.arrayOf(PropTypes.string),
      polyphen2_hdiv_rankscore: PropTypes.number,
      polyphen2_hdiv_score: PropTypes.arrayOf(PropTypes.number),
      polyphen2_hvar_pred: PropTypes.arrayOf(PropTypes.string),
      polyphen2_hvar_rankscore: PropTypes.number,
      polyphen2_hvar_score: PropTypes.arrayOf(PropTypes.number),
    })
  ),
  evePathogenicityType: number,
};

PathogenicityScoresCard.defaultProps = {
  newThresholds: null,
  acmgRules: null,
  acmgAnnotation: null,
  shouldShowNewComponent: false,
  insilicoData: null,
  thresholds: null,
  dbnsfp: null,
  dann: null,
  cadd: null,
  dbnsfpDbscsnv: null,
  dbnsfpPremium: null,
  evePathogenicityType: null,
};

const mapStateToProps = (state) => ({
  acmgRules: getACMGVerdictClassifications(state),
  thresholds: getInSilicoThresholds(state),
  newThresholds: getNewInSilicoThresholds(state),
  dbnsfp: getDbnsfp(state),
  dann: getDann(state),
  cadd: getCadd(state),
  dbnsfpPremium: getDbnsfpPremium(state),
  dbnsfpDbscsnv: getDbnsfpDbscsnv(state),
  evePathogenicityType: getEveScorePathogenicityType(state),
  shouldShowNewComponent: !isThaliaVersionSmallerThanElevenPointFour(state),
  insilicoData: getAllInsilcoData(state),
  acmgAnnotation: getAcmgAnnotation(state),
});

export default connect(mapStateToProps)(PathogenicityScoresCard);
